import {
  RecommendationPublishParams,
  RecommendationsStateResponse,
} from '../../api/recommendations.js';
import { Action } from '../../types/action.js';
import {
  RecommendationResponse,
  RecommendationsState,
  RecommendationStatus,
  RecommendationType,
} from '../../types/Recommendations.js';
import {
  acceptRecommendationActions,
  publishRecommendationActions,
  recommendationActions,
  recommendationsActions,
  recommendationsStateActions,
  rejectRecommendationActions,
} from '../action/api/RecommendationsApi.js';

const initialState: RecommendationsState = {
  isLoading: false,
  hasNew: null,
  items: [],
  publishSuggestionStatuses: [],
  page: 1,
  pages: 1,
  error: '',
};

type PayloadType =
  | RecommendationResponse
  | RecommendationType
  | RecommendationsStateResponse
  | RecommendationPublishParams
  | null
  | undefined
  | string;

const isRecommendationResponse = (
  payload: PayloadType,
): payload is RecommendationResponse =>
  !!(payload as RecommendationResponse)?.items;

const isRecommendationType = (
  payload: PayloadType,
): payload is RecommendationType => !!(payload as RecommendationType)?.id;

const hasNewRecommendations = (
  recommendations: RecommendationType[],
): boolean =>
  !!recommendations.find((item) => item.status === RecommendationStatus.ACTIVE);

const updateItemOrCreate = (
  items: RecommendationType[],
  item: RecommendationType,
): RecommendationType[] => {
  if (!items.length) {
    return [item];
  }

  return items.map((el) =>
    el.id === item.id ? item : el,
  ) as RecommendationType[];
};

const reducer = (
  state: RecommendationsState = initialState,
  action: Action<PayloadType>,
) => {
  switch (action.type) {
    case recommendationActions.type.REQUEST:
    case recommendationsActions.type.REQUEST:
    case acceptRecommendationActions.type.REQUEST:
    case rejectRecommendationActions.type.REQUEST: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }

    case recommendationsStateActions.type.REQUEST: {
      return {
        ...state,
        // items: [],
        isLoading: true,
        hasNew: null,
      };
    }

    case publishRecommendationActions.type.REQUEST: {
      const { recommendationId } =
        action.payload as RecommendationPublishParams;

      return {
        ...state,
        publishSuggestionStatuses: [
          ...state.publishSuggestionStatuses,
          { id: recommendationId },
        ],
      };
    }

    case recommendationsActions.type.FAILED:
    case recommendationsStateActions.type.FAILED: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case recommendationActions.type.FAILED:
    case acceptRecommendationActions.type.FAILED:
    case rejectRecommendationActions.type.FAILED: {
      return {
        ...state,
        isLoading: false,
        error: typeof action.payload === 'string' ? action.payload : '',
      };
    }

    case recommendationsActions.type.SUCCESS: {
      const { items, page, pages } = isRecommendationResponse(action.payload)
        ? action.payload
        : state;

      return {
        ...state,
        error: '',
        isLoading: false,
        hasNew:
          state.hasNew === null ? hasNewRecommendations(items) : state.hasNew,
        items,
        page,
        pages,
      };
    }

    case recommendationsStateActions.type.SUCCESS: {
      return {
        ...state,
        isLoading: false,
        hasNew: (action.payload as RecommendationsStateResponse).activeExists,
      };
    }

    case recommendationActions.type.SUCCESS:
    case acceptRecommendationActions.type.SUCCESS:
    case rejectRecommendationActions.type.SUCCESS: {
      const items: RecommendationType[] = isRecommendationType(action.payload)
        ? updateItemOrCreate(state.items, action.payload)
        : state.items;

      return {
        ...state,
        error: '',
        isLoading: false,
        hasNew: hasNewRecommendations(items),
        items,
      };
    }

    case publishRecommendationActions.type.SUCCESS: {
      const items: RecommendationType[] = isRecommendationType(action.payload)
        ? updateItemOrCreate(state.items, action.payload)
        : state.items;

      return {
        ...state,
        error: '',
        isLoading: false,
        hasNew: hasNewRecommendations(items),
        items,
        publishSuggestionStatuses: state.publishSuggestionStatuses.filter(
          (el) => el.id !== (action.payload as RecommendationType).id,
        ),
      };
    }

    case publishRecommendationActions.type.FAILED: {
      return {
        ...state,
        error: '',
        isLoading: false,
        publishSuggestionStatuses: state.publishSuggestionStatuses.map((el) =>
          el.id === (action.payload as RecommendationType).id
            ? { ...el, hasError: true }
            : el,
        ),
      };
    }

    default:
      return state;
  }
};

export default reducer;
